import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AllLocationsListApiResponse,
  IFetchLocation,
} from '@/services/location/location.type';
import {
  ICreateLocation,
  IDeleteLocation,
  ILocation,
  IUpdateLocation,
  ApiResponse,
  ILimit,
} from '../sagas/locationSaga';

export interface LocationState {
  locations: ApiResponse;
  loading: boolean;
  error: boolean;
  location: ILocation;
  locationsList: AllLocationsListApiResponse;
  lastFetchedOperatorId?: string;
  message?: string;
}

export const initialState: LocationState = {
  locations: {
    data: [],
    pagination: {
      totalRecords: 0,
      totalItems: 0,
      currentPage: 0,
      totalPages: 0,
      nextPage: 0,
      previousPage: 0,
    },
    success: false,
    message: '',
  },
  loading: true,
  error: false,
  location: {
    locationId: '',
    name: '',
    sourceLocationId: '',
    operatorId: '',
    createdOn: '',
    updatedOn: '',
  },
  locationsList: {
    success: false,
    message: '',
    data: [],
    pagination: {
      totalRecords: 0,
      currentPage: 0,
      totalPages: 0,
      nextPage: 0,
      previousPage: 0,
    },
  },
  lastFetchedOperatorId: undefined,
};

const locationSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    getLocations: (state, action: PayloadAction<ILimit>) => {
      state.loading = true;
    },
    getLocationsSuccess: (state, action: PayloadAction<ApiResponse>) => {
      state.loading = false;
      state.locations = { ...action.payload };
    },
    getLocationsFailure: (state) => {
      state.loading = false;
      state.error = true;
    },
    getLocation: (state) => {
      state.loading = true;
    },
    getLocationSuccess: (state, action: PayloadAction<ILocation>) => {
      state.loading = false;
      state.location = { ...action.payload }; // Avoid direct assignment, create a new object
    },
    getLocationFailure: (state) => {
      state.loading = false;
      state.error = true;
    },
    createLocation: (state, action: PayloadAction<ICreateLocation>) => {
      state.loading = true;
    },
    createLocationSuccess: (state, action: PayloadAction<ILocation>) => {
      state.loading = false;
      state.location = { ...action.payload }; // Avoid direct assignment, create a new object
    },
    createLocationFailure: (state) => {
      state.loading = false;
      state.error = false;
    },
    updateLocation: (state, action: PayloadAction<IUpdateLocation>) => {
      state.loading = true;
    },
    updateLocationSuccess: (state, action: PayloadAction<ILocation>) => {
      state.loading = false;
      state.location = { ...action.payload }; // Avoid direct assignment, create a new object
    },
    updateLocationFailure: (state) => {
      state.loading = false;
      state.error = false;
    },
    deleteLocation: (state, action: PayloadAction<IDeleteLocation>) => {
      state.loading = true;
    },
    deleteLocationSuccess: (state, action: PayloadAction<ILocation>) => {
      state.loading = false;
      state = {
        ...state,
        locations: {
          ...state.locations,
          data: state.locations.data.filter(
            (item) => item.locationId !== action.payload.locationId,
          ),
        },
      }; // Avoid direct assignment, create a new object
    },
    deleteLocationFailure: (state) => {
      state.loading = false;
      state.error = false;
    },
    // List API
    getLocationsList: (state, action: PayloadAction<IFetchLocation>) => {
      state.loading = true;
      state.message = '';
    },
    getLocationsListSuccess: (
      state,
      action: PayloadAction<
        AllLocationsListApiResponse & { operatorId?: string }
      >,
    ) => {
      state.loading = false;
      const { data, pagination, success, message, operatorId } = action.payload;
      const isSameOperator = state.lastFetchedOperatorId === operatorId;
      const isNextPage = pagination?.currentPage > 1 && isSameOperator;
      state.error = false;
      state.message = action.payload.message;
      state.locationsList = {
        success,
        message,
        pagination,
        data: isNextPage ? [...state.locationsList.data, ...data] : data,
      };

      state.lastFetchedOperatorId = operatorId;
    },
    getLocationsListFailure: (state, action: any) => {
      state.loading = false;
      state.error = true;
      state.message = action.payload?.messageDetails?.length
        ? action.payload?.messageDetails[0]?.message
        : action.payload?.message;
    },
    clearLocationError: (state) => {
      state.error = false;
      state.message = ''; // Reset message to empty string as per initial state
    },
  },
});

const locationReducer = locationSlice.reducer;

export const {
  getLocations,
  getLocationsSuccess,
  getLocationFailure,
  getLocation,
  getLocationSuccess,
  getLocationsFailure,
  createLocation,
  createLocationSuccess,
  createLocationFailure,
  updateLocation,
  updateLocationFailure,
  updateLocationSuccess,
  deleteLocation,
  deleteLocationSuccess,
  deleteLocationFailure,
  getLocationsList,
  getLocationsListSuccess,
  getLocationsListFailure,
  clearLocationError,
} = locationSlice.actions;

export default locationReducer;
